import React from "react";
import { DollarSign } from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  ConversationThread, FeatureCallout, AgentCard,
  TimelineList, SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogRachelTonematchedCollections = () => (
  <RichBlogLayout
    category="Patient Billing"
    eyebrow="Patient Collections"
    title="Rachel: Tone-Matched Patient Collections That Actually Get Paid"
    subtitle="Structured cadence, adaptive channel choice, contextual escalation — collections that don't feel like collections."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Prose>
      <p>
        Look at any practice's aging report on a Friday afternoon and you can
        see the story. The fresh balances at 0–30 days are mostly fine. The
        balances at 60 days have grown. The 90-day column is where the
        practice has quietly decided to stop trying. By 120 days most of these
        accounts are no longer collections — they are write-offs in waiting.
      </p>
      <p>
        It is not because the team isn't trying. A biller might make 20 to 30
        outreach calls in a day, between everything else they are doing. Even
        the best biller is going to lose track of who they called Tuesday, who
        prefers texts, who said they would pay after the 15th, who stopped
        responding three weeks ago. The work does not scale to a full chair
        schedule.
      </p>
    </Prose>

    <AgentCard
      name="Rachel"
      role="AI Patient Collections · SMS + Voice"
      initial="R"
      description="Rachel runs intelligent, tone-matched billing outreach across SMS and live voice for every open patient balance. She remembers statement history, prior payments, and how each patient prefers to communicate — and she paces the conversation so it never tips into nagging."
      channels={["Outbound SMS", "Outbound voice", "Statement history aware", "Contextual escalation"]}
    />

    <Prose>
      <h2>Why balances quietly become write-offs</h2>
      <p>
        A practice with 600 active monthly patients typically carries 200+
        open patient balances on any given Friday afternoon. At 20–30 calls
        per biller per day, a single human can touch each balance maybe once
        every two weeks — if nothing else hits the queue. The 90-day column
        doesn't grow because the team gave up. It grows because the math
        doesn't work.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 200, prefix: "+", label: "Balances actively worked at once" },
        { value: 5, label: "Outreach windows over 120 days" },
        { value: 24, suffix: "/7", label: "Continuously running" },
        { value: 0, label: "Balances forgotten or skipped" },
      ]}
    />

    <Prose>
      <p>
        Rachel does not add a biller. She adds the patient memory and the
        consistency that a single biller cannot maintain across hundreds of
        accounts. Every balance is on its own track. Every outreach builds on
        the last one.
      </p>

      <h2>The reminder-blast model is what trains patients to ignore you</h2>
      <p>
        The mental model most practices have for "automated collections" is
        the same SMS reminder going out to everyone, escalating to a colder
        version of the same SMS reminder, and finally an aggressive voice
        message that nobody listens to. That model is what trains patients to
        ignore collections. It is the opposite of what Rachel is.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="Generic dunning workflow"
      afterTitle="With Rachel"
      beforeItems={[
        "Same templated reminder sent to everyone, regardless of history",
        "One-channel — usually SMS — even when the patient prefers calls",
        "No memory of past payments or stated commitments",
        "Eventually escalates to the same cold, transactional language",
      ]}
      afterItems={[
        "Tone, language, and channel adapted per patient on every send",
        "Switches between SMS and live voice based on observed response patterns",
        "Reads statement and payment history; references it explicitly",
        "Escalates with context — never restarts from zero with the patient",
      ]}
    />

    <Prose>
      <h2>The cadence — 14, 30, 60, 90, 120 days</h2>
      <p>
        Every patient balance enters a structured outreach cadence the moment
        the patient responsibility is finalized. The first two weeks are an
        initial outreach window — typically the most likely to convert. From
        there, additional follow-ups land at 30, 60, 90, and 120 days, each
        with a different intent and tone.
      </p>
    </Prose>

    <TimelineList
      title="Patient balance cadence · $312.40 · Maria Lopez"
      events={[
        { time: "Day 0", channel: "sms", label: "Itemized statement delivered", by: "Rachel · text-to-pay link included" },
        { time: "Day 5", channel: "sms", label: "Friendly check-in · payment reminder", by: "Rachel · matches patient's casual tone" },
        { time: "Day 12", channel: "sms", label: "Second reminder with split-pay option", by: "Rachel · adapted after no response" },
        { time: "Day 30", channel: "call", label: "Live voice outreach · explanatory tone", by: "Rachel · channel switch" },
        { time: "Day 60", channel: "sms", label: "Statement + payment plan offer", by: "Rachel · plan terms drafted" },
        { time: "Day 90", channel: "call", label: "Voice outreach · clear options laid out", by: "Rachel · firmer but not cold" },
        { time: "Day 120", channel: "form", label: "Escalated to biller with full context", by: "Routed to Jamie · all history attached" },
      ]}
    />

    <Prose>
      <h2>Tone-matching, in practice</h2>
      <p>
        The phrase "tone-matched" is doing a lot of work here, so it is worth
        unpacking what it actually means in a real thread. Rachel reads how a
        patient has communicated with the practice in the past — formal,
        casual, terse, chatty — and writes back in roughly the same register.
      </p>
      <p>
        The patient experience is that they are texting with someone who
        sounds like a normal human who already knows them. Not a no-reply
        robot. Not a "FINAL NOTICE" template.
      </p>
    </Prose>

    <ConversationThread
      title="Day 12 follow-up · SMS thread"
      channel="SMS"
      messages={[
        { from: "system", text: "Cadence: 12 days since statement · no response yet" },
        { from: "agent", text: "Hi Maria — just wanted to circle back on the $312.40 from your visit on April 14. No rush, just wanted to make sure it didn't get lost in the inbox. If it's easier, we can split it into two payments." },
        { from: "patient", text: "oh shoot yes sorry — can i do half this week and half on the 1st?" },
        { from: "agent", text: "Of course. I'll send the link for $156.20 right now and queue the second one for May 1. Sound good?" },
        { from: "patient", text: "perfect ty" },
        { from: "system", text: "Payment plan created · $156.20 today · $156.20 on May 1 · ledger updated" },
      ]}
    />

    <PullQuote>
      A balance does not become a write-off because nobody cared. It becomes
      a write-off because nobody could remember whose Tuesday it was.
    </PullQuote>

    <Prose>
      <h2>The decisions Rachel makes before each message</h2>
      <p>
        Rachel is not running a single rule. She is making three decisions
        every time a balance is due for outreach: which channel, what tone,
        and how much to ask for. Each of those decisions is informed by what
        has happened in the thread before.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Reads the patient's communication history",
          body:
            "Past texts, past calls, past statement opens. If the patient consistently responds to SMS within hours, Rachel keeps texting. If SMS is silent for two outreach windows, she switches to voice.",
        },
        {
          title: "Picks the channel for this specific balance",
          body:
            "Channel choice is per-message, not per-patient. A small balance might stay on SMS forever; a larger balance escalates to a live voice call earlier in the cadence.",
        },
        {
          title: "Drafts the message in the patient's register",
          body:
            "Casual patients get casual language. Formal patients get formal language. Spanish-speaking patients get Spanish. Each draft references the relevant claim and amount, never a generic template.",
        },
        {
          title: "Paces against fatigue",
          body:
            "Intentional gaps between outreach bursts. Rachel will not message a patient three times in a week. She will not text a patient who just got a clinical message from the practice the same day.",
        },
        {
          title: "Escalates with context, not from scratch",
          body:
            "When a balance crosses 120 days unresolved, Rachel does not just hand off a name and number. She hands off the full thread, the offers extended, and the patient's stated reasons.",
        },
      ]}
    />

    <Prose>
      <h2>What gets handed back to the team</h2>
      <p>
        The escalation is the most important part. Most automated collections
        either never escalate (the balance just sits) or escalate badly (a
        biller gets a name with no idea what was already tried). Rachel
        escalates with the receipts. The biller picks up a thread already
        seven turns deep, with the offered split-pay terms and the patient's
        last reply visible.
      </p>
    </Prose>

    <FeatureCallout
      stat="120 days"
      label="From statement to resolution — without a chase"
      description="Every balance moves through five outreach windows in the right tone, on the right channel, and only escalates to a human when the structured cadence runs out of room."
      icon={DollarSign}
    />

    <Prose>
      <h2>What's left for the human team</h2>
      <p>
        The biller who used to spend mornings dialing through an aging report
        spends them on the small set of accounts where Rachel asked for help —
        a payment plan that needs approval, a patient who wants to dispute a
        charge, a balance that has crossed the legitimate write-off threshold.
        The 90-day column shrinks because the work happened earlier and more
        consistently than a single human ever could have done it.
      </p>
      <p>
        The patient experience also quietly improves. Nobody is being yelled
        at by a "FINAL NOTICE" template. Statements are itemized, the offer
        to split a payment shows up at the right moment, and the message
        sounds like it came from a person.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        Collections is a memory and consistency problem disguised as a tone
        problem. Rachel solves all three at once — a structured 14, 30, 60, 90,
        120-day cadence that adapts channel and tone per patient, escalates
        with full context, and converts the kind of balances that quietly
        become write-offs.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogRachelTonematchedCollections;
